import { loadGoogleFont } from "utils/googleFonts";
import "resources/styles/template2.css";
import { Form, FormInstance } from "antd";
import { useEffect } from "react";
import useGetLogo from "../hooks/useGetLogo";
import { sanitizeHtml } from "utils/sanitizeHtml";

interface FontOption {
  value: unknown;
  label: { props?: { children?: string } };
}

export default function Template2({
  form,
  newFontsOptions,
  companyData,
  companyBankAccount: _companyBankAccount,
}: {
  form: FormInstance;
  newFontsOptions: FontOption[];
  companyData: {
    company: GetCompanyWithAddressCompany;
    address: AddressResult;
  };
  companyBankAccount?: BankAccountResult;
}): React.ReactElement {
  const logoDescription = Form.useWatch(["logoDescription"], form);
  const tableBody = Form.useWatch(["tableBorder"], form);
  const tableHeaderColor = Form.useWatch(["baseTextColorBackground"], form);
  const watermarkOpacity = Form.useWatch(["backgroundImageOpacity"], form);
  const baseColor = Form.useWatch(["baseColor"], form);
  const baseTextColor = Form.useWatch(["baseTextColor"], form);
  const titleColor = Form.useWatch(["titleColor"], form);
  const font = Form.useWatch(["font"], form);
  const showName = Form.useWatch(["showName"], form);
  const showDescription = Form.useWatch(["showDescription"], form);
  const showStampSpace = Form.useWatch(["showStampSpace"], form);
  const showMentionAndLegalStatus = Form.useWatch(["showMentionAndLegalStatus"], form);
  const logo = Form.useWatch(["logo"], form);
  const hasLogo = logo?.file?.status !== "uploading";
  const note = Form.useWatch(["note"], form);
  const latePaymentPenalty = Form.useWatch(["latePaymentPenalty"], form);
  const fixedRecoveryFee = Form.useWatch(["fixedRecoveryFee"], form);
  const discount = Form.useWatch(["discount"], form);
  const backgroundImage = Form.useWatch(["backgroundImage"], form);
  const logoPosition = Form.useWatch(["logoPosition"], form);
  const fontName = newFontsOptions?.find((item: FontOption) => item?.value === font)?.label?.props
    ?.children;
  const showDeliveryAddress = Form.useWatch(["showDeliveryAddress"], form);
  const { generateByLogo, accountingFirmName } = useGetLogo();

  const address = companyData?.address;
  const company = companyData?.company;
  const isIntreprenneur = company?.legalCategory?.code === "1000";

  useEffect(() => {
    if (fontName) {
      loadGoogleFont(fontName);
    }
  }, [fontName]);

  const fontStyle = fontName ? { fontFamily: `"${fontName}", sans-serif` } : {};

  return (
    <div className="A4Container" style={fontStyle}>
      {backgroundImage && (
        <div
          className="background-image"
          style={{
            opacity: watermarkOpacity || 0.05,
          }}
        >
          <img src={backgroundImage} alt="" />
        </div>
      )}
      <header className="default-header2">
        {logoPosition === "right" ? (
          <>
            <div
              className={`invoice-title2 ${logoPosition === "right" ? "title-left" : ""}`}
              style={{
                borderBottomColor: baseColor || "#e0e0e0",
              }}
            >
              <h1 style={{ color: titleColor || "#000", ...fontStyle }}>FACTURE</h1>
              <p style={{ color: titleColor || "#000", ...fontStyle }}>
                <b>FACTURE N°:</b> F-123456789
              </p>
              <p style={{ color: titleColor || "#000", ...fontStyle }}>
                <b>DATE:</b> {new Date().toLocaleDateString("fr-FR")}
              </p>
            </div>
            <div className={`invoice-logo2 logo-position2-${logoPosition || "left"}`}>
              {hasLogo ? (
                <img src={logo} alt="" />
              ) : (
                <div className="logo-placeholder" style={{ borderColor: baseColor || "#e0e0e0" }}>
                  <span style={{ color: baseTextColor || "#000" }}>LOGO</span>
                </div>
              )}
              <div
                className="logo-description2"
                style={{ color: titleColor || "#000", ...fontStyle }}
              >
                {logoDescription}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className={`invoice-logo2 logo-position2-${logoPosition || "left"}`}>
              {hasLogo ? (
                <img src={logo} alt="" />
              ) : (
                <div className="logo-placeholder" style={{ borderColor: baseColor || "#e0e0e0" }}>
                  <span style={{ color: baseTextColor || "#000" }}>LOGO</span>
                </div>
              )}
              <div
                className="logo-description2"
                style={{ color: titleColor || "#000", ...fontStyle }}
              >
                {logoDescription}
              </div>
            </div>
            <div
              className="invoice-title2"
              style={{
                borderBottomColor: baseColor || "#e0e0e0",
              }}
            >
              <h1 style={{ color: titleColor || "#000", ...fontStyle }}>FACTURE</h1>
              <p style={{ color: titleColor || "#000", ...fontStyle }}>
                <b>FACTURE N°:</b> F-123456789
              </p>
              <p style={{ color: titleColor || "#000", ...fontStyle }}>
                <b>DATE:</b> {new Date().toLocaleDateString("fr-FR")}
              </p>
            </div>
          </>
        )}
      </header>
      <br />
      <div
        className="address-container2"
        style={{
          borderColor: baseColor || "#e0e0e0",
        }}
      >
        <div
          className="company-address2"
          style={{ color: baseTextColor || "#000", borderRightColor: baseColor || "#e0e0e0" }}
        >
          <address>
            <b>{company?.displayName || company?.name}</b>
            <span>{address?.address1 || ""}</span>
            <span>{address?.address2 || ""}</span>
            <span>
              {address?.postalCode || ""}, {address?.city || ""}, {address?.country?.name || ""}
            </span>
            <span>{company?.email || ""}</span>
            <span>{company?.phone1 || ""}</span>
          </address>
        </div>
        <div className="customer-address2" style={{ color: baseTextColor || "#000" }}>
          {showDeliveryAddress ? (
            <div className="address-grid2">
              <address className="main-customer-address2">
                <b>ACME CORPORATION</b>
                <span>123 Business Avenue</span>
                <span>Suite 456</span>
                <span>75001 Paris, France</span>
                <span>contact@acme-corp.com</span>
                <span>+33 1 23 45 67 89</span>
              </address>
              <div className="delivery-address2" style={{ color: baseTextColor || "#000" }}>
                <address>
                  <b style={{ textDecoration: "underline", color: titleColor || "#000" }}>
                    Adresse de livraison:
                  </b>
                  <b>ACME CORPORATION - ENTREPÔT</b>
                  <span>789 Avenue de la Logistique</span>
                  <span>Zone Industrielle</span>
                  <span>75019 Paris, France</span>
                </address>
              </div>
            </div>
          ) : (
            <address className="single-customer-address2">
              <b>ACME CORPORATION</b>
              <span>123 Business Avenue</span>
              <span>Suite 456</span>
              <span>75001 Paris, France</span>
              <span>contact@acme-corp.com</span>
              <span>+33 1 23 45 67 89</span>
            </address>
          )}
        </div>
      </div>
      <br />
      <div
        className="info-container2"
        style={{
          borderColor: baseColor || "#e0e0e0",
          color: baseTextColor || "#000",
        }}
      >
        <div className="info-item-left2">
          <div>
            <b>TVA Intracom:</b>
            <span>{company?.euVatNumber}</span>
          </div>
          <div>
            <b>Siret:</b>
            <span>{company?.vatNumber}</span>
          </div>
          <div>
            <b>Code APE:</b>
            <span>{company?.businessActivity?.code}</span>
          </div>
          <div>
            <b>Forme juridique:</b>
            <span>{company?.legalStructure?.code}</span>
          </div>
          <div>
            <b>RCS:</b>
            <span>{company?.rcs}</span>
          </div>
        </div>
        <div className="info-item-right2">
          <div>
            <b>Nº Client:</b>
            <span>CLI-789123456</span>
          </div>
          <div>
            <b>Siret:</b>
            <span>12345678901234</span>
          </div>
          <div>
            <b>TVA Intracom:</b>
            <span>FR12345678901</span>
          </div>
        </div>
      </div>
      <section
        className="default-body2"
        style={{ minHeight: "0px", color: baseTextColor || "#000" }}
      >
        {(showName || showDescription) && (
          <div className="extra-info-container2">
            <div className="extra-info-column2">
              {showName && (
                <small className="extra-info-name2" style={{ color: baseTextColor || "#000" }}>
                  Refonte site web e-commerce
                </small>
              )}
            </div>
            <div className="extra-info-column2">
              {showDescription && (
                <small
                  className="extra-info-description2"
                  style={{ color: baseTextColor || "#000" }}
                >
                  Développement complet d&apos;une plateforme e-commerce avec système de paiement
                  intégré et interface d&apos;administration avancée
                </small>
              )}
            </div>
          </div>
        )}
        <br />
        <div className={`default-table2 body-${tableBody || "border_none"}`}>
          <table
            style={{
              borderColor: baseColor || "#000",
            }}
          >
            <thead
              id="customColumnHeader"
              className="default-table__thead2"
              style={{
                backgroundColor: tableHeaderColor || "#ddd",
                borderColor: baseColor || "#000",
                color: titleColor || "#000",
              }}
            >
              <tr style={{ borderColor: baseColor || "#000" }}>
                <td
                  colSpan={1}
                  className="default-table__column-left"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  <b>REF</b>
                </td>
                <td
                  colSpan={3}
                  className="default-table__column-left"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  <b>DÉSIGNATION</b>
                </td>
                <td
                  colSpan={1}
                  className="default-table__column-right"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  <b>PU HT</b>
                </td>
                <td
                  colSpan={1}
                  className="default-table__column-right"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  <b>QTÉ</b>
                </td>
                <td
                  colSpan={1}
                  className="default-table__column-right"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  <b>UNITÉ</b>
                </td>
                <td
                  colSpan={1}
                  className="default-table__column-right"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  <b>MONTANT HT</b>
                </td>
                <td
                  colSpan={1}
                  className="default-table__column-right"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  <b>TVA %</b>
                </td>
              </tr>
            </thead>
            <tbody>
              <tr className="row" style={{ borderColor: baseColor || "#000" }}>
                <td
                  colSpan={1}
                  className="line-description default-default__lines__column-left2"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  <span>SERV-001</span>
                </td>
                <td
                  colSpan={3}
                  className="default-table__column-left2"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  Consultation développement web
                </td>
                <td
                  colSpan={1}
                  className="default-table__column-right2"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  150.00
                </td>
                <td
                  colSpan={1}
                  className="default-table__column-right2"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  8
                </td>
                <td
                  colSpan={1}
                  className="default-table__column-right2"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  HEURE
                </td>
                <td
                  id="td-vatPct"
                  colSpan={1}
                  className="default-table__column-right2"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  1,200.00
                </td>
                <td
                  colSpan={1}
                  className="default-table__column-right2"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  20.00%
                </td>
              </tr>
              <tr className="row" style={{ borderColor: baseColor || "#000" }}>
                <td
                  colSpan={1}
                  className="line-description default-default__lines__column-left2"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  <span>PROD-002</span>
                </td>
                <td
                  colSpan={3}
                  className="default-table__column-left2"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  Ordinateur portable professionnel
                </td>
                <td
                  colSpan={1}
                  className="default-table__column-right2"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  899.00
                </td>
                <td
                  colSpan={1}
                  className="default-table__column-right2"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  2
                </td>
                <td
                  colSpan={1}
                  className="default-table__column-right2"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  UNITAIRE
                </td>
                <td
                  id="td-vatPct"
                  colSpan={1}
                  className="default-table__column-right2"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  1,798.00
                </td>
                <td
                  colSpan={1}
                  className="default-table__column-right2"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  20.00%
                </td>
              </tr>
              <tr className="row" style={{ borderColor: baseColor || "#000" }}>
                <td
                  colSpan={1}
                  className="line-description default-default__lines__column-left2"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  <span>LIC-003</span>
                </td>
                <td
                  colSpan={3}
                  className="default-table__column-left2 "
                  style={{ borderColor: baseColor || "#000" }}
                >
                  Licence logiciel annuelle
                </td>
                <td
                  colSpan={1}
                  className="default-table__column-right2"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  450.00
                </td>
                <td
                  colSpan={1}
                  className="default-table__column-right2"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  1
                </td>
                <td
                  colSpan={1}
                  className="default-table__column-right2"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  UNITAIRE
                </td>
                <td
                  id="td-vatPct"
                  colSpan={1}
                  className="default-table__column-right2"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  450.00
                </td>
                <td
                  colSpan={1}
                  className="default-table__column-right2"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  20.00%
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="invoice-totals2" style={{ color: baseTextColor || "#666" }}>
          <div className="default-payments2">
            <div className="payment-row">
              <span className="payment-label">MODE DE PAIEMENT:</span>
              <span className="payment-value">BANCAIRE</span>
            </div>
            <div className="payment-row">
              <span className="payment-label">ECHÉANCE:</span>
              <span className="payment-value">{new Date().toLocaleDateString("fr-FR")}</span>
            </div>
            <div className="payment-row">
              <span className="payment-label">COORD. BANCAIRES:</span>
            </div>
            <div className="payment-row">
              <span className="payment-label">BANQUE:</span>
              <span className="payment-value">{"Crédit Agricole Provence Côte d'Azur"}</span>
            </div>
            <div className="payment-row">
              <span className="payment-label">BIC/SWIFT:</span>
              <span className="payment-value">AGRIFRPP881</span>
            </div>
            <div className="payment-row">
              <span className="payment-label">IBAN:</span>
              <span className="payment-value">FR76 1881 9000 1234 5678 9012 345</span>
            </div>
          </div>
          <table className="default-totals2" style={{ borderColor: baseColor || "#e0e0e0" }}>
            <tbody>
              <tr style={{ borderColor: baseColor || "#f0f0f0" }}>
                <td style={{ borderColor: baseColor || "#f0f0f0", color: baseTextColor || "#666" }}>
                  TOTAL HT
                </td>
                <td style={{ color: baseTextColor || "#333" }}>€3,448.00</td>
              </tr>
              <tr style={{ borderColor: baseColor || "#f0f0f0" }}>
                <td style={{ borderColor: baseColor || "#f0f0f0", color: baseTextColor || "#666" }}>
                  TOTAL HT NET
                </td>
                <td style={{ color: baseTextColor || "#333" }}>€3,448.00</td>
              </tr>
              <tr style={{ borderColor: baseColor || "#f0f0f0" }}>
                <td style={{ borderColor: baseColor || "#f0f0f0", color: baseTextColor || "#666" }}>
                  TVA 20%
                </td>
                <td style={{ color: baseTextColor || "#333" }}>€689.60</td>
              </tr>
              <tr className="total-ttc" style={{ borderTopColor: baseColor || "#d0d0d0" }}>
                <td style={{ color: baseTextColor || "#000" }}>
                  <strong>TOTAL TTC</strong>
                </td>
                <td style={{ color: baseTextColor || "#000" }}>
                  <strong>€4,137.60</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="default-terms-and-conditions2" style={{ color: baseTextColor || "#000" }}>
          <div className="terms-content2">
            {showMentionAndLegalStatus && (
              <div
                className="payment-penalty-terms2"
                style={{ borderColor: baseColor || "#d1d5db", color: baseTextColor || "#000" }}
              >
                <span>
                  Article L441-6 du code du commerce, tout retard de paiement donnera lieu à
                  {
                    "l'application d'une pénalité égale à trois fois le taux d'intérêt légal. En sus"
                  }
                  des pénalités de retard, une indemnité forfaitaire pour frais de recouvrement de
                  {"40 € est applicable. Pas d'escompte pour règlement anticipé."}
                </span>
              </div>
            )}

            {note && (
              <div className="note-terms2">
                <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(note) }} />
              </div>
            )}
            {latePaymentPenalty && (
              <div className="extra-terms2">
                <span
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(latePaymentPenalty || "") }}
                />
              </div>
            )}
            {fixedRecoveryFee && (
              <div className="extra-terms2">
                <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(fixedRecoveryFee || "") }} />
              </div>
            )}

            {discount && (
              <div className="extra-terms2">
                <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(discount || "") }} />
              </div>
            )}
            {isIntreprenneur && (
              <div className="extra-terms2">
                <span>TVA non applicable, article 293 B du CGI.</span>
              </div>
            )}
          </div>

          {showStampSpace && (
            <div className="stamp-space-section2" style={{ borderColor: baseColor || "#9ca3af" }}>
              <span>Cachet et signature</span>
            </div>
          )}
        </div>
      </section>
      <footer className="default-footer2" style={{ color: baseTextColor || "#000" }}>
        <div className="footer-container2">
          <div className="generated-by2" style={{ borderBottomColor: baseColor || "#e5e7eb" }}>
            <span>Document généré par</span>
            <strong>NeoFact</strong>
            <span>by</span>
            <img
              src={generateByLogo ?? undefined}
              alt={accountingFirmName ?? undefined}
              className="newrai-logo2"
            />
          </div>
          <div className="company-footer-info2" style={{ color: baseTextColor || "#6b7280" }}>
            <span className="company-name2">{company?.displayName || company?.name}</span>
            <span className="company-details2">
              {address?.address1}, {address?.postalCode} {address?.city}, {address?.country?.name} •
              TVA: {company?.euVatNumber} • SIRET: {company?.vatNumber} • APE:{" "}
              {company?.businessActivity?.code} •{company?.legalStructure?.code} • {company?.email}{" "}
              • {company?.phone1}
              {company?.website && ` • ${company?.website}`}
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
